import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnInit,
  output,
  signal
} from '@angular/core';
import {FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {RecordData, RecordsDataRequestQueryParams} from "../../../../../../types/records-table.types";
import {
  defaultRecordsDataRequestQueryParams
} from "../../../../../../models/default-records-data-request-query-params";
import {BloggerModalService} from "../../../services/blogger-modal.service";
import {bloggerModalResourceType, newBloggerFormFields} from '../../../configs/blogger-modal-config';
import {BloggerProfileDialogData} from "../../../types/blogger-modal.types";
import {NewBloggerProfileService} from "../../../services/new-blogger-profile.service";
import {MatTab, MatTabGroup} from "@angular/material/tabs";
import {MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {MatButton} from "@angular/material/button";
import {MatDivider, MatListOption, MatSelectionList, MatSelectionListChange} from "@angular/material/list";
import {ModalsService} from "../../../../../../services/modals.service";
import {CloseModalReason} from "../../../../../../types/modal.types";
import {BloggerInfoComponent} from "../../../components/blogger-info/blogger-info.component";

@Component({
  selector: 'app-new-blogger-profile-modal',
  standalone: true,
  imports: [
    MatTabGroup,
    MatTab,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatButton,
    FormsModule,
    MatSelectionList,
    MatListOption,
    MatDivider,
    BloggerInfoComponent
  ],
  templateUrl: './new-blogger-profile-modal.component.html',
  styleUrl: './new-blogger-profile-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewBloggerProfileModalComponent implements OnInit {
  bloggerFormGroup = input<FormGroup>(new FormGroup({}));
  bloggerDialogData = input.required<BloggerProfileDialogData>();
  initBloggerCardHeader = output<RecordData>();
  toggleLoadingState = output<boolean>();
  handleCloseModal = output<CloseModalReason>();

  private bloggerModalService: BloggerModalService = inject(BloggerModalService);
  private newBloggerProfileService: NewBloggerProfileService = inject(NewBloggerProfileService);
  private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);
  private modalsService: ModalsService = inject(ModalsService);

  public readonly newBloggerFormFields = newBloggerFormFields;
  public selectedBlogger = signal<RecordData | null>(null);
  public bloggers = signal<Array<RecordData>>([]);
  public hasBloggers = signal<boolean>(false);
  public searchBloggerNameOrUrl = signal<string>('');
  private recordsDataRequestQueryParams = signal<RecordsDataRequestQueryParams>(defaultRecordsDataRequestQueryParams);

  public async ngOnInit(): Promise<void> {
    this.searchBloggerNameOrUrl.set(this.bloggerDialogData().recordData['channelUrl'] as string);
    await this.loadBloggers();
  }

  public async handleCreateNewBlogger(): Promise<void> {
    this.bloggerFormGroup().disable();

    await this.bloggerModalService.addNewBloggerProfile(bloggerModalResourceType.path, this.bloggerFormGroup().value)
      .then((recordData) => {
        this.selectedBlogger.set(recordData);
        this.handleSelectBlogger();
      })
      .catch(() => this.handleError());
  }

  public async handleSelectBlogger(): Promise<void> {
    if (!this.selectedBlogger()) { return; }

    const bloggerData = this.selectedBlogger();
    if (!bloggerData) { return; }

    const attachProps = this.newBloggerProfileService.createAttachProps(this.bloggerDialogData().recordData, Number(bloggerData?.id));
    await this.bloggerModalService.attachRecordData(attachProps)
      .catch(() => this.handleCloseModal.emit('cancel'));
    this.initBloggerCardHeader.emit(bloggerData);
    this.changeDetectorRef.detectChanges();
  }

  public handleOpenBloggerModal(blogger: RecordData) {
    this.modalsService.getStatusFromBloggerCardModal({
      columnParamKey: 'bloggerModal',
      columnParam: this.bloggerDialogData().columnParam,
      recordData: blogger,
      path: bloggerModalResourceType.path
    });
  }

  public async loadBloggers(): Promise<void> {
    this.toggleLoadingState.emit(true);
    if (this.searchBloggerNameOrUrl) {
      this.searchInBlogger(this.searchBloggerNameOrUrl());
    }

    await this.bloggerModalService.getSearchBloggers(this.searchBloggerNameOrUrl())
      .then((data) => this.bloggers.set(data))
      .catch(() => this.bloggers.set([]));
    this.hasBloggers.set(!!this.bloggers().length);
    this.toggleLoadingState.emit(false);
  }

  public handleUpdateSearchValue(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchBloggerNameOrUrl.set(value);
  }

  public handleSelectionListChange(event: MatSelectionListChange): void {
    this.selectedBlogger.set(event.options.at(0)?.value);
  }

  private searchInBlogger(searchBloggerNameOrUrl: string): void {
    this.recordsDataRequestQueryParams.set({
      ...defaultRecordsDataRequestQueryParams,
      size: undefined,
      search: searchBloggerNameOrUrl
    });
  }

  private handleError(): void {
    this.bloggerFormGroup().enable();
  }
}
